import {
  ChangeDetectorRef,
  Component,
  DestroyRef,
  Input,
  OnChanges,
  OnInit,
  SimpleChanges,
  inject,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, RouterModule } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { TooltipModule } from 'primeng/tooltip';
import { ButtonModule } from 'primeng/button';
import { DrawerModule } from 'primeng/drawer';
import { MessageService } from 'primeng/api';
import { forkJoin } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { EquipamentoFilterDrawerComponent } from '../components/equipamento-filter-drawer/equipamento-filter-drawer.component';
import { EquipamentoFilterStateService } from '../services/equipamento-filter-state.service';
import {
  EquipmentCountsService,
  EquipmentTypeKey,
} from '../services/equipment-counts.service';

interface EquipOption {
  val: string;
  text: string;
}

interface ActionConfig {
  val: string;
  icon: string;
  cls: string;
  label: string;
  desc: string;
}

const EQUIP_DATA: Record<string, {
  label: string;
  options: EquipOption[];
  accoes: (estado: string) => ActionConfig[];
}> = {
  viatura: {
    label: "— Selecionar Viatura —",
    options: [
      { val: "LD-45-89-AB|ativo", text: "LD-45-89-AB — Toyota Hilux 2022 (Banco BFA)" },
      { val: "LD-89-12-EF|ativo", text: "LD-89-12-EF — Mitsubishi L200 2020 (Unitel HQ)" },
      { val: "LD-23-67-CD|manutencao", text: "LD-23-67-CD — Nissan NP300 2021 (Sonangol EP)" },
      { val: "LD-34-56-GH|inativo", text: "LD-34-56-GH — Ford Ranger 2019 (TAAG)" },
      { val: "LD-11-22-IJ|suspenso", text: "LD-11-22-IJ — Land Cruiser 2021 (Hotel Trópico)" }
    ],
    accoes: (estado: string) => {
      const base = [
        { val: "manutencao", icon: "fa-wrench", cls: "a-yellow", label: "Enviar para Manutenção", desc: "Registar entrada em oficina" },
        { val: "suspenso", icon: "fa-pause-circle", cls: "a-red", label: "Suspender", desc: "Suspensão temporária" },
        { val: "inativo", icon: "fa-times-circle", cls: "a-red", label: "Inativar", desc: "Retirar de operação" }
      ];
      if (estado !== "ativo") {
        base.push({ val: "ativo", icon: "fa-play-circle", cls: "a-green", label: "Reativar", desc: "Devolver a operação" });
      }
      return base;
    }
  },
  arma: {
    label: "— Selecionar Arma —",
    options: [
      { val: "ARM-2024-0156|atribuida", text: "ARM-2024-0156 — Pistola Glock G17 (João Manuel)" },
      { val: "ARM-2024-0089|atribuida", text: "ARM-2024-0089 — Espingarda Mossberg (Carlos Mendes)" },
      { val: "ARM-2024-0201|deposito", text: "ARM-2024-0201 — Pistola Beretta 92 (Depósito)" },
      { val: "ARM-2023-0045|manutencao", text: "ARM-2023-0045 — Revólver S&W .38 (Em Manutenção)" },
      { val: "ARM-2022-0012|suspenso", text: "ARM-2022-0012 — Pistola SIG Sauer P320 (Suspensa)" }
    ],
    accoes: (estado: string) => {
      const base = [
        { val: "verificacao", icon: "fa-clipboard-check", cls: "a-purple", label: "Registar Verificação", desc: "Inspeção periódica obrigatória" },
        { val: "manutencao", icon: "fa-wrench", cls: "a-yellow", label: "Enviar para Manutenção", desc: "Reparação ou limpeza profissional" },
        { val: "deposito", icon: "fa-box-archive", cls: "active", label: "Enviar para Depósito", desc: "Recolher e armazenar" },
        { val: "suspenso", icon: "fa-pause-circle", cls: "a-red", label: "Suspender", desc: "Suspensão por irregularidade" }
      ];
      if (estado !== "atribuida") {
        base.push({ val: "atribuida", icon: "fa-user-plus", cls: "a-green", label: "Atribuir / Reativar", desc: "Atribuir a um guardia" });
      }
      return base;
    }
  },
  utensilio: {
    label: "— Selecionar Utensílio —",
    options: [
      { val: "UT-001|bom", text: "UT-001 — Lanterna LED Profissional (Banco BFA)" },
      { val: "UT-002|desgastado", text: "UT-002 — Rádio Motorola DP2400 (Sonangol EP)" },
      { val: "UT-003|bom", text: "UT-003 — Colete Reflector (TAAG)" },
      { val: "UT-004|avariado", text: "UT-004 — Bastão Tático (Unitel)" },
      { val: "UT-005|desgastado", text: "UT-005 — Rádio Motorola DP2400 (Banco BFA)" },
      { val: "UT-006|bom", text: "UT-006 — Lanterna de Mão (BNA)" }
    ],
    accoes: (estado: string) => [
      { val: "inspecao", icon: "fa-clipboard-check", cls: "a-purple", label: "Registar Inspeção", desc: "Verificação de estado e funcionamento" },
      { val: "manutencao", icon: "fa-wrench", cls: "a-yellow", label: "Enviar para Manutenção/Reparação", desc: "Enviar para reparação" },
      { val: "suspenso", icon: "fa-pause-circle", cls: "a-red", label: "Suspender / Retirar", desc: "Retirar temporariamente de serviço" },
      { val: "abate", icon: "fa-trash-alt", cls: "a-red", label: "Abater / Substituir", desc: "Marcado para substituição" }
    ]
  }
};

@Component({
  selector: 'app-equipamentos-nav',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    FormsModule,
    TooltipModule,
    ButtonModule,
    DrawerModule,
    EquipamentoFilterDrawerComponent
  ],
  templateUrl: './equipamentos-nav.component.html',
  styleUrl: './equipamentos-nav.component.scss'
})
export class EquipamentosNavComponent implements OnInit, OnChanges {
  @Input() currentType: EquipmentTypeKey | null = null;
  @Input() currentTotal: number | null = null;

  private readonly filterSvc      = inject(EquipamentoFilterStateService);
  private readonly destroyRef     = inject(DestroyRef);
  private readonly cdr            = inject(ChangeDetectorRef);
  private readonly messageService = inject(MessageService);
  private readonly countsService  = inject(EquipmentCountsService);
  private readonly router         = inject(Router);

  totalViaturas   = 0;
  totalArmas      = 0;
  totalUtensilios = 0;
  loadingCounts   = true;
  private otherCountsLoaded = false;

  showFiltersDrawer = false;
  activeFilterCount = 0;

  // Controle de Estado do Drawer "Gerir Equipamento"
  showGerirDrawer = false;
  gerirTipo: 'viatura' | 'arma' | 'utensilio' = 'viatura';
  selectedEquipVal = '';
  selectedEquipId = '';
  selectedEquipEstado = '';
  selectedAccao = '';
  selectedAccaoLabel = '';

  dataInicio = '';
  dataFim = '';
  motivoTxt = '';

  readonly equipData = EQUIP_DATA;

  get currentEquipOptions(): EquipOption[] {
    return this.equipData[this.gerirTipo]?.options || [];
  }

  get currentAccoes(): ActionConfig[] {
    if (!this.selectedEquipEstado) return [];
    return this.equipData[this.gerirTipo]?.accoes(this.selectedEquipEstado) || [];
  }

  get dataInicioLabel(): string {
    if (this.selectedAccao === 'inspecao' || this.selectedAccao === 'verificacao') return 'Data da Inspeção *';
    if (this.selectedAccao === 'manutencao') return 'Data de Entrada em Oficina *';
    return 'Data *';
  }

  get dataFimLabel(): string {
    if (this.selectedAccao === 'inspecao' || this.selectedAccao === 'verificacao') return 'Próxima Inspeção';
    if (this.selectedAccao === 'manutencao') return 'Data Prevista de Saída';
    return 'Data de Retorno Prevista';
  }

  get motivoLabel(): string {
    if (this.selectedAccao === 'inspecao' || this.selectedAccao === 'verificacao') return 'Observações';
    if (this.selectedAccao === 'manutencao') return 'Tipo de Intervenção / Descrição *';
    return 'Motivo / Justificação *';
  }

  get motivoPlaceholder(): string {
    if (this.selectedAccao === 'inspecao' || this.selectedAccao === 'verificacao') return 'Introduza observações sobre o estado do equipamento...';
    if (this.selectedAccao === 'manutencao') return 'Descreva a intervenção a realizar...';
    return 'Descreva o motivo desta acção...';
  }

  get confirmBtnLabel(): string {
    return this.selectedAccaoLabel || 'Confirmar';
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['currentTotal'] || changes['currentType']) {
      this.applyCurrentTotal();
      this.updateLoadingState();
    }
  }

  ngOnInit(): void {
    this.applyCurrentTotal();
    this.loadOtherCounts();

    // Escuta alterações do serviço de estado do filtro
    this.filterSvc.filters$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((filters: any) => {
        let count = 0;
        if (filters.Estado) count++;
        if (filters.Ativo !== null && filters.Ativo !== undefined) count++;
        if (filters.IdPosto) count++;
        if (filters.DataAquisicao) count++;
        if (filters.Search) count++;
        if (filters.IdTipoEquipamento || filters.categoria) count++;
        
        this.activeFilterCount = count;

        // Tenta redirecionar caso a categoria venha atualizada no serviço de estado
        this.checkAndRedirect(filters);

        this.cdr.detectChanges();
      });
  }

  /**
   * Método executado ao receber o evento de aplicação de filtro do drawer
   */
  onApplyFilter(filterData: any): void {
    this.showFiltersDrawer = false;
    this.checkAndRedirect(filterData);
  }

  /**
   * Executa a navegação de rota baseando-se em qualquer formato que o objeto de filtro traga
   */
  private checkAndRedirect(filterObj: any): void {
    if (!filterObj) return;

    // Obtém o valor do tipo/categoria de diversas propriedades comuns
    const cat = (
      filterObj.categoria ||
      filterObj.IdTipoEquipamento ||
      filterObj.tipoEquipamento ||
      filterObj.tipo ||
      ''
    )
      .toString()
      .toLowerCase();

    if (!cat) return;

   if (cat.includes('viatura') || cat === '2') {
      this.router.navigate(['/admin/equipamentos/viaturas']);
    } else if (cat.includes('arma') || cat === '1') {
      this.router.navigate(['/admin/equipamentos/armas']);
    } else if (cat.includes('utensilio') || cat.includes('utensílio') || cat === '3') {
      this.router.navigate(['/admin/equipamentos/utensilios']);
    }
  }

  private loadOtherCounts(): void {
    const types: EquipmentTypeKey[] = ['viaturas', 'armas', 'utensilios'];
    const pendingTypes = types.filter((type) => type !== this.currentType);

    if (!pendingTypes.length) {
      this.otherCountsLoaded = true;
      this.updateLoadingState();
      return;
    }

    forkJoin(pendingTypes.map((type) => this.countsService.getTotal(type)))
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (totals) => {
          pendingTypes.forEach((type, index) =>
            this.setDisplayedTotal(type, totals[index] ?? 0),
          );
          this.otherCountsLoaded = true;
          this.updateLoadingState();
        },
        error: () => {
          this.otherCountsLoaded = true;
          this.updateLoadingState();
        },
      });
  }

  private applyCurrentTotal(): void {
    if (!this.currentType || this.currentTotal === null) return;
    this.setDisplayedTotal(this.currentType, this.currentTotal);
    this.countsService.setTotal(this.currentType, this.currentTotal);
  }

  private setDisplayedTotal(type: EquipmentTypeKey, total: number): void {
    if (type === 'viaturas') this.totalViaturas = total;
    if (type === 'armas') this.totalArmas = total;
    if (type === 'utensilios') this.totalUtensilios = total;
  }

  private updateLoadingState(): void {
    const currentReady = !this.currentType || this.currentTotal !== null;
    this.loadingCounts = !this.otherCountsLoaded || !currentReady;
    this.cdr.markForCheck();
  }

  openGerirDrawer(): void {
    this.showGerirDrawer = true;
    this.resetDrawer();
  }

  resetDrawer(): void {
    this.gerirTipo = 'viatura';
    this.selectedEquipVal = '';
    this.selectedEquipId = '';
    this.selectedEquipEstado = '';
    this.selectedAccao = '';
    this.selectedAccaoLabel = '';
    this.dataInicio = '';
    this.dataFim = '';
    this.motivoTxt = '';
  }

  selectTipo(tipo: 'viatura' | 'arma' | 'utensilio'): void {
    this.gerirTipo = tipo;
    this.selectedEquipVal = '';
    this.selectedEquipId = '';
    this.selectedEquipEstado = '';
    this.selectedAccao = '';
    this.selectedAccaoLabel = '';
    this.dataInicio = '';
    this.dataFim = '';
    this.motivoTxt = '';
  }

  onEquipChange(): void {
    if (!this.selectedEquipVal) {
      this.selectedEquipId = '';
      this.selectedEquipEstado = '';
      this.selectedAccao = '';
      this.selectedAccaoLabel = '';
      return;
    }
    const parts = this.selectedEquipVal.split('|');
    this.selectedEquipId = parts[0];
    this.selectedEquipEstado = parts[1] || '';
    this.selectedAccao = '';
    this.selectedAccaoLabel = '';
  }

  selectAccao(accaoVal: string, accaoLabel: string): void {
    this.selectedAccao = accaoVal;
    this.selectedAccaoLabel = accaoLabel;
    if (!this.dataInicio) {
      this.dataInicio = new Date().toISOString().split('T')[0];
    }
  }

  getEstadoClass(estado: string): string {
    const classes: Record<string, string> = {
      ativo: 'green',
      manutencao: 'yellow',
      inativo: 'red',
      suspenso: 'grey',
      atribuida: 'green',
      deposito: 'blue-bd',
      bom: 'green',
      desgastado: 'yellow',
      avariado: 'red'
    };
    return classes[estado] || '';
  }

  getEstadoLabel(estado: string): string {
    const labels: Record<string, string> = {
      ativo: '🟢 Ativo',
      manutencao: '🟡 Em Manutenção',
      inativo: '🔴 Inativo',
      suspenso: '⏸ Suspenso',
      atribuida: '🟢 Atribuída',
      deposito: '🔵 Em Depósito',
      bom: '🟢 Bom Estado',
      desgastado: '🟡 Desgastado',
      avariado: '🔴 Avariado'
    };
    return labels[estado] || estado;
  }

  confirmarGerirEquipamento(): void {
    if (!this.selectedEquipVal) {
      this.messageService.add({ severity: 'error', summary: 'Erro', detail: 'Selecione um equipamento.', life: 3000 });
      return;
    }
    if (!this.selectedAccao) {
      this.messageService.add({ severity: 'error', summary: 'Erro', detail: 'Selecione uma acção.', life: 3000 });
      return;
    }
    if (!this.dataInicio) {
      this.messageService.add({ severity: 'error', summary: 'Erro', detail: 'Indique a data.', life: 3000 });
      return;
    }
    if (this.selectedAccao !== 'inspecao' && this.selectedAccao !== 'verificacao' && !this.motivoTxt.trim()) {
      this.messageService.add({ severity: 'error', summary: 'Erro', detail: 'Preencha o motivo/observações.', life: 3000 });
      return;
    }

    const accoesMsg: Record<string, string> = {
      manutencao: `🔧 ${this.selectedEquipId} enviado para manutenção em ${this.dataInicio}.`,
      inspecao: `📋 Inspeção de ${this.selectedEquipId} registada em ${this.dataInicio}.`,
      verificacao: `🔒 Verificação de ${this.selectedEquipId} registada em ${this.dataInicio}.`,
      suspenso: `⏸ ${this.selectedEquipId} suspenso em ${this.dataInicio}.`,
      inativo: `🔴 ${this.selectedEquipId} marcado como inativo.`,
      ativo: `✅ ${this.selectedEquipId} reativado com sucesso!`,
      deposito: `📦 ${this.selectedEquipId} recolhido ao depósito.`,
      abate: `🗑️ ${this.selectedEquipId} marcado para abate/substituição.`,
      atribuida: `👤 Abertura para atribuição de ${this.selectedEquipId}.`
    };

    this.showGerirDrawer = false;
    this.messageService.add({
      severity: 'success',
      summary: 'Sucesso',
      detail: accoesMsg[this.selectedAccao] || `Acção aplicada a ${this.selectedEquipId}.`,
      life: 4000
    });

    this.resetDrawer();
  }
}